import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSettingParams } from '../../../common/filterCompany/useSettingParams'
import { UseI18n } from '../../../common/hooks/useI18n'
import { Loading } from '../../../common/loading'
import { Notification } from '../../../common/notification'
import { Panel } from '../../../common/panel'
import { Table } from '../../../common/table'
import { valToPercent } from '../../../helpers/value'
import { selectPeriod } from '../../common/period/slice'
import { UseScoreRange } from '../common/hooks/useScoreRange'
import { GroupNote } from '../common/html/GroupNote'
import {
  COLUMN_ID_TABLE,
  ITEM_ANY_POTENTIAL,
  ITEM_ANY_RISK,
  TITLE_TABLE_SCORE_TREND,
  VALUE_SCORE,
} from '../constant'
import { selectScoreActive } from '../store/slice'
import { PopupScoreTrend } from './components/PopupScoreTrend'
import { CompanyNumber } from './components/schemaTable/CompanyNumber'
import { Level } from './components/schemaTable/Level'
import { Trend } from './components/schemaTable/Trend'
import {
  getDataTablePerRedux,
  getDataTableRedux,
  getFragment,
  getLoadingTrend,
  selectIdsTable,
  selectIdsTablePer,
} from './store/slice'
import { getPerformanceTrend, getRiskTrend } from './store/thunk'

const TEXT_BY_TYPE_SCORE = {
  [VALUE_SCORE.CREDIT_SCORE]: {
    title: 'portfolioScore.riskTrend',
    textNote: 'portfolioScore.textNoteRiskTrend',
  },
  [VALUE_SCORE.PERFORMANCE]: {
    title: 'portfolioScore.performanceTrend',
    textNote: 'portfolioScore.textNotePerformanceTrend',
  },
}

export const ScoreTrend = ({ isDynamicReport, type }) => {
  const dispatch = useDispatch()

  const { value: typeValueScore } = useSelector(selectScoreActive)
  const idsRisk = useSelector(selectIdsTable)
  const idsPer = useSelector(selectIdsTablePer)
  const period = useSelector(selectPeriod)
  const isLoading = useSelector(getLoadingTrend)
  const locale = useSelector((state) => state.i18n.locale)
  const fragment = useSelector(getFragment)

  const typeValue = isDynamicReport ? type : typeValueScore
  const ids = typeValue === VALUE_SCORE.CREDIT_SCORE ? idsRisk : idsPer
  const getReduxTable =
    typeValue === VALUE_SCORE.CREDIT_SCORE
      ? getDataTableRedux
      : getDataTablePerRedux
  const itemAnyByScore = useMemo(
    () =>
      typeValueScore === VALUE_SCORE.CREDIT_SCORE
        ? ITEM_ANY_RISK
        : ITEM_ANY_POTENTIAL,
    [typeValue],
  )

  const paramsSetting = useSettingParams()
  const { data: dataRangeScore, loading } = UseScoreRange()

  const [isShowPopupEdit, setShowPopupEdit] = useState(false)

  const renderLevel = (itemData) => (
    <Level
      data={itemData}
      dataRange={
        typeValue === VALUE_SCORE.CREDIT_SCORE
          ? [...dataRangeScore?.CREDIT_RISK, itemAnyByScore]
          : [...dataRangeScore?.PERFORMANCE, itemAnyByScore]
      }
    />
  )

  const schema = TITLE_TABLE_SCORE_TREND.map((item) => {
    const colId = item.key
    const title = UseI18n(item.title)

    return {
      colId,
      title,
      thStyle: item.thStyle,
      tdStyle: item.tdStyle,
      render: (data, { rowId }) => {
        switch (colId) {
          case COLUMN_ID_TABLE.TREND:
            return <Trend data={data} />
          case COLUMN_ID_TABLE.PREVIOUS:
            return <>{renderLevel(data)}</>
          case COLUMN_ID_TABLE.CURRENT:
            return <>{renderLevel(data)}</>
          case COLUMN_ID_TABLE.PERCENT:
            return valToPercent(data, locale, false, false, 0)
          case COLUMN_ID_TABLE.NUMBER_OF_COMPANY:
            return (
              <CompanyNumber
                data={data}
                rowId={rowId}
                period={period}
                typeValue={typeValue}
                isDynamicReport={isDynamicReport}
              />
            )
          default:
            return data
        }
      },
    }
  })

  const renderTable = (dynamic) => {
    if (dynamic) {
      return (
        <Table
          ids={ids}
          getDataFromRedux={getReduxTable}
          schema={schema}
          isNoStickyHead={true}
          isDisplayScroll={false}
          isDynamicReport
        />
      )
    }
    return (
      <Table
        ids={ids}
        getDataFromRedux={getReduxTable}
        schema={schema}
        autoHeightMax={600}
      />
    )
  }

  const onOpenPopupEdit = () => {
    setShowPopupEdit(true)
  }

  const onClosePopup = () => {
    setShowPopupEdit(false)
  }

  useEffect(() => {
    if (!!isDynamicReport) return
    if (typeValue === VALUE_SCORE.CREDIT_SCORE) {
      dispatch(
        getRiskTrend({
          timeFrequency: period,
          ...paramsSetting,
        }),
      )
    }
    if (typeValue === VALUE_SCORE.PERFORMANCE) {
      dispatch(
        getPerformanceTrend({
          timeFrequency: period,
          ...paramsSetting,
        }),
      )
    }
  }, [typeValue, fragment, period, paramsSetting])

  if (isDynamicReport) {
    if (!ids.length) return <Notification isDynamicReport />
    return renderTable(isDynamicReport)
  }

  return (
    <>
      <Panel
        label={TEXT_BY_TYPE_SCORE[typeValue].title}
        isI18n
        style={{ minHeight: 200 }}
        downloadJpgParams={{
          arrName: [UseI18n(TEXT_BY_TYPE_SCORE[typeValue].title)],
          titleJpg: UseI18n(TEXT_BY_TYPE_SCORE[typeValue].title),
          DownloadComponent: (
            <div style={{ width: 1200 }}>
              <Table
                ids={ids}
                getDataFromRedux={getReduxTable}
                schema={schema}
                isDisplayScroll={false}
                isNoStickyHead={true}
              />
            </div>
          ),
        }}
      >
        {isLoading || loading ? (
          <div className="positionRelative h100">
            <Loading />
          </div>
        ) : !ids.length ? (
          <Notification />
        ) : (
          <div className="flexColumn">
            <GroupNote
              title={TEXT_BY_TYPE_SCORE[typeValue].textNote}
              onClickEdit={onOpenPopupEdit}
              isShowEdit
            />
            <div className="flex1">{renderTable()}</div>
          </div>
        )}
      </Panel>
      {isShowPopupEdit && (
        <PopupScoreTrend
          isShow={isShowPopupEdit}
          onClose={onClosePopup}
          title={TEXT_BY_TYPE_SCORE[typeValue].title}
          typeValue={typeValue}
        />
      )}
    </>
  )
}

ScoreTrend.propTypes = {
  isDynamicReport: PropTypes.bool,
}

ScoreTrend.defaultProps = {
  isDynamicReport: false,
}
